import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { getActivityLogs } from "../../services/dataService";
import { dayTimeLabel } from "../../utils/queuePresentation";
import { useAuth } from "../../contexts/AuthContext";
import { tokens } from "../../utils/figmaTokens";

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const { user } = useAuth();

  // Fetch activity logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const activityLogs = await getActivityLogs(200);
      setLogs(activityLogs);
      setFilteredLogs(activityLogs);
    } catch (err) {
      console.error("Error fetching activity logs:", err);
      setError("Failed to load activity logs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Filter logs based on search term and action filter
  useEffect(() => {
    let filtered = logs;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.userName?.toLowerCase().includes(searchLower) ||
          log.userEmail?.toLowerCase().includes(searchLower) ||
          log.action?.toLowerCase().includes(searchLower) ||
          log.entityType?.toLowerCase().includes(searchLower) ||
          log.entityName?.toLowerCase().includes(searchLower) ||
          log.description?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by action type
    if (filterAction !== "all") {
      filtered = filtered.filter((log) => log.action === filterAction);
    }

    setFilteredLogs(filtered);
  }, [searchTerm, filterAction, logs]);

  // Get unique action types for filter
  const uniqueActions = Array.from(
    new Set(logs.map((log) => log.action).filter(Boolean))
  ).sort();

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const label = dayTimeLabel(timestamp);
    return label === "-" ? "N/A" : label;
  };

  // Format action string (e.g., "UPDATE_STUDENT" -> "Updated Student")
  const formatActionString = (action) => {
    if (!action) return "Unknown";
    // 047 D4: staff read "Completed". The stored code keeps its name.
    if (action === "LESSON_STAFF_FINISH") return "Lesson Staff Complete";
    // Check if it's in uppercase with underscores format
    if (action.includes("_") && action === action.toUpperCase()) {
      const parts = action.split("_");
      let verb = parts[0].toLowerCase();
      if (verb === "update") verb = "Updated";
      else if (verb === "create") verb = "Created";
      else if (verb === "delete") verb = "Deleted";
      else if (verb === "add") verb = "Added";
      else if (verb === "remove") verb = "Removed";
      else verb = verb.charAt(0).toUpperCase() + verb.slice(1);

      const rest = parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
      return [verb, ...rest].join(" ");
    }
    return action;
  };

  // Get action color
  const getActionColor = (action) => {
    if (!action) return "default";
    const actionLower = action.toLowerCase();
    if (actionLower.includes("create") || actionLower.includes("add")) {
      return "success";
    }
    if (actionLower.includes("update") || actionLower.includes("edit")) {
      return "info";
    }
    if (actionLower.includes("delete") || actionLower.includes("remove")) {
      return "error";
    }
    if (actionLower.includes("view") || actionLower.includes("read")) {
      return "primary";
    }
    return "default";
  };

  // Handle row click to show details
  const handleRowClick = (log) => {
    setSelectedLog(log);
    setDetailsDialogOpen(true);
  };

  // Format details object for display
  const formatDetails = (details) => {
    if (!details || typeof details !== "object") {
      return "No details available";
    }

    if (Object.keys(details).length === 0) {
      return "No changes recorded";
    }

    return Object.entries(details).map(([key, value]) => {
      let displayValue = value;

      // Handle arrays
      if (Array.isArray(value)) {
        displayValue = value.length > 0 ? value.join(", ") : "[]";
      }
      // Handle objects
      else if (typeof value === "object" && value !== null) {
        displayValue = JSON.stringify(value, null, 2);
      }
      // Handle null/undefined
      else if (value === null || value === undefined) {
        displayValue = "null";
      }
      // Handle booleans
      else if (typeof value === "boolean") {
        displayValue = value ? "true" : "false";
      }
      // Handle numbers
      else if (typeof value === "number") {
        displayValue = value.toString();
      }
      // Handle strings
      else {
        displayValue = String(value);
      }

      return { key, value: displayValue };
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchLogs}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: 0, 
      width: "100%",
      fontFamily: "'Poppins', sans-serif",
      "& .MuiTypography-root, & .MuiTableCell-root, & .MuiButton-root, & .MuiInputBase-input, & .MuiMenuItem-root, & .MuiChip-label, & .MuiAlert-message, & .MuiDialogTitle-root, & .MuiListItemText-primary, & .MuiListItemText-secondary": {
        fontFamily: "'Poppins', sans-serif !important"
      }
    }}>
      {/* Header */}
      <Box
        sx={{
          mb: { xs: 2, sm: 4 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "12px", sm: "14px" } }}
          >
            Activity Log / Overview
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              mt: 0.5,
              fontSize: { xs: "20px", sm: "28px" },
            }}
          >
            Activity Log
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchLogs}
          disabled={loading}
          sx={{
            borderRadius: "8px",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            "&:hover": {
              color: tokens.brand.on,
              backgroundColor: tokens.brand.primary,
            }
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: tokens.shadow.raised,
          borderRadius: "12px",
          border: `1px solid ${tokens.border.default}`,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: "50%",
                bgcolor: tokens.brand.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: tokens.brand.on,
              }}
            >
              <HistoryIcon />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", fontSize: { xs: "18px", sm: "24px" } }}
              >
                {filteredLogs.length} Activity {filteredLogs.length === 1 ? "Entry" : "Entries"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {logs.length} total entries in system
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card
        sx={{
          mb: 3,
          boxShadow: tokens.shadow.raised,
          borderRadius: "12px",
          border: `1px solid ${tokens.border.default}`,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: "100%", sm: 300 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={filterAction}
                label="Action Type"
                onChange={(e) => setFilterAction(e.target.value)}
                sx={{ borderRadius: "8px" }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                {uniqueActions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {formatActionString(action)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Activity Logs Table */}
      <Card
        sx={{
          boxShadow: tokens.shadow.raised,
          borderRadius: "12px",
          border: `1px solid ${tokens.border.default}`,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: 2 } }}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              border: `1px solid ${tokens.border.default}`,
              overflowX: "auto",
              maxWidth: "100%",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: tokens.surface.sunken }}>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${tokens.border.default}`,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${tokens.border.default}`,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Action
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${tokens.border.default}`,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    Entity
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${tokens.border.default}`,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${tokens.border.default}`,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Timestamp
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${tokens.border.default}`,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      width: 100,
                    }}
                  >
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm || filterAction !== "all"
                          ? "No activities match your filters."
                          : "No activity logs found."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow
                      key={log.id}
                      sx={{
                        "&:hover": { backgroundColor: tokens.hover, cursor: "pointer" },
                      }}
                      onClick={() => handleRowClick(log)}
                    >
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: tokens.brand.primary,
                              color: tokens.brand.on,
                              width: 32,
                              height: 32,
                              fontSize: "0.875rem",
                            }}
                          >
                            {log.userName?.charAt(0) || "U"}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium", fontSize: "0.875rem" }}
                            >
                              {log.userName || "Unknown User"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {log.userEmail || "No email"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <Chip
                          label={formatActionString(log.action)}
                          size="small"
                          color={getActionColor(log.action)}
                          sx={{ borderRadius: "50px" }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          display: { xs: "none", md: "table-cell" },
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: "600", textTransform: "capitalize" }}>
                            {log.entityType || "N/A"}
                          </Typography>
                          {log.entityName && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {log.entityName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            maxWidth: { xs: 200, sm: 400 },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={log.description}
                        >
                          {log.description || "No description"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                          {formatTimestamp(log.timestamp || log.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(log);
                          }}
                          sx={{ color: "primary.main" }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Activity Details
            </Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              {/* Basic Information */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}
                >
                  Basic Information
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="User"
                      secondary={`${selectedLog.userName || "Unknown"} (${selectedLog.userEmail || "No email"})`}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Action"
                      secondary={
                        <Chip
                          label={formatActionString(selectedLog.action)}
                          size="small"
                          color={getActionColor(selectedLog.action)}
                          sx={{ borderRadius: "6px", mt: 0.5 }}
                        />
                      }
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Entity Type"
                      secondary={selectedLog.entityType || "N/A"}
                      secondaryTypographyProps={{ sx: { textTransform: "capitalize" } }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Entity Name"
                      secondary={selectedLog.entityName || "N/A"}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Entity ID"
                      secondary={selectedLog.entityId || "N/A"}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Timestamp"
                      secondary={formatTimestamp(selectedLog.timestamp || selectedLog.createdAt)}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Description"
                      secondary={selectedLog.description || "No description"}
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Changes Made - Before/After Comparison */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 2, color: "text.secondary" }}
                >
                  Changes Made (Before → After)
                </Typography>
                {selectedLog.before &&
                  selectedLog.after &&
                  typeof selectedLog.before === "object" &&
                  typeof selectedLog.after === "object" ? (
                  <Box
                    sx={{
                      bgcolor: tokens.surface.muted,
                      borderRadius: "8px",
                      p: 2,
                      maxHeight: 500,
                      overflowY: "auto",
                    }}
                  >
                    {(() => {
                      // Get all unique keys from both before and after
                      const allKeys = new Set([
                        ...Object.keys(selectedLog.before || {}),
                        ...Object.keys(selectedLog.after || {}),
                      ]);

                      if (allKeys.size === 0) {
                        return (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                            No changes recorded
                          </Typography>
                        );
                      }

                      return (
                        <List dense>
                          {Array.from(allKeys).map((key, index) => {
                            const beforeValue = selectedLog.before[key];
                            const afterValue = selectedLog.after[key];
                            const hasChanged = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);

                            // Format values for display
                            const formatValue = (value) => {
                              if (value === null || value === undefined) return "—";
                              if (Array.isArray(value)) {
                                return value.length > 0 ? value.join(", ") : "[]";
                              }
                              if (typeof value === "object") {
                                return JSON.stringify(value, null, 2);
                              }
                              if (typeof value === "boolean") {
                                return value ? "true" : "false";
                              }
                              return String(value);
                            };

                            const beforeFormatted = formatValue(beforeValue);
                            const afterFormatted = formatValue(afterValue);

                            return (
                              <ListItem
                                key={key}
                                sx={{
                                  px: 0,
                                  py: 1.5,
                                  borderBottom:
                                    index < allKeys.size - 1
                                      ? `1px solid ${tokens.border.strong}`
                                      : "none",
                                  bgcolor: hasChanged ? tokens.warning.bg : "transparent",
                                  borderRadius: hasChanged ? "4px" : "0",
                                  mb: hasChanged ? 1 : 0,
                                }}
                              >
                                <Box sx={{ width: "100%" }}>
                                  <Typography
                                    variant="body2"
                                    sx={(theme) => ({
                                      fontWeight: "bold",
                                      color: hasChanged ? "error.main" : "primary.main",
                                      mb: 1,
                                      // In dark, MUI's red is dim on the amber row: the kit's ink colour (047 D114).
                                      ...(hasChanged && theme.applyStyles("dark", { color: tokens.error.ink })),
                                    })}
                                  >
                                    {key}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                      gap: 2,
                                    }}
                                  >
                                    {/* Before Value */}
                                    <Box
                                      sx={{
                                        bgcolor: tokens.error.bg,
                                        p: 1.5,
                                        borderRadius: "4px",
                                        border: `1px solid ${tokens.error.border}`,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={(theme) => ({
                                          fontWeight: "bold",
                                          color: "error.main",
                                          display: "block",
                                          mb: 0.5,
                                          ...theme.applyStyles("dark", { color: tokens.error.ink }),
                                        })}
                                      >
                                        BEFORE
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "monospace",
                                          fontSize: "0.875rem",
                                          wordBreak: "break-word",
                                          color: "text.primary",
                                        }}
                                      >
                                        {beforeFormatted}
                                      </Typography>
                                    </Box>

                                    {/* After Value */}
                                    <Box
                                      sx={{
                                        bgcolor: tokens.success.bg,
                                        p: 1.5,
                                        borderRadius: "4px",
                                        border: `1px solid ${tokens.success.border}`,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={(theme) => ({
                                          fontWeight: "bold",
                                          color: "success.main",
                                          display: "block",
                                          mb: 0.5,
                                          ...theme.applyStyles("dark", { color: tokens.success.ink }),
                                        })}
                                      >
                                        AFTER
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "monospace",
                                          fontSize: "0.875rem",
                                          wordBreak: "break-word",
                                          color: "text.primary",
                                        }}
                                      >
                                        {afterFormatted}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {!hasChanged && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mt: 0.5, display: "block", fontStyle: "italic" }}
                                    >
                                      No change
                                    </Typography>
                                  )}
                                </Box>
                              </ListItem>
                            );
                          })}
                        </List>
                      );
                    })()}
                  </Box>
                ) : selectedLog.details &&
                  typeof selectedLog.details === "object" &&
                  Object.keys(selectedLog.details).length > 0 ? (
                  // Fallback to old format if before/after not available
                  <Box
                    sx={{
                      bgcolor: tokens.surface.muted,
                      borderRadius: "8px",
                      p: 2,
                      maxHeight: 400,
                      overflowY: "auto",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Note: Only new values are available (before/after comparison not recorded)
                    </Typography>
                    <List dense>
                      {formatDetails(selectedLog.details).map((item, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            px: 0,
                            py: 1,
                            borderBottom:
                              index < formatDetails(selectedLog.details).length - 1
                                ? `1px solid ${tokens.border.strong}`
                                : "none",
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold", color: "primary.main" }}
                              >
                                {item.key}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.5,
                                  fontFamily: "monospace",
                                  bgcolor: tokens.surface.default,
                                  p: 1,
                                  borderRadius: "4px",
                                  wordBreak: "break-word",
                                }}
                              >
                                {item.value}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      bgcolor: tokens.surface.muted,
                      borderRadius: "8px",
                      p: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No specific changes recorded
                    </Typography>
                  </Box>
                )}
              </Box>

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityLog;

